import React from 'react'
import { RefreshCw, XCircle, AlertTriangle } from 'lucide-react'
import WorkPanelWrapper from '../common/WorkPanelWrapper'

interface BalanceCheck {
  beginningBalance: number
  endingBalance: number
  expectedEnding: number
  difference: number
  totalCredits: number
  totalDebits: number
  transactionCount: number
}

interface ValidationIssue {
  field: string
  severity: 'error' | 'warning' | 'info'
  message: string
}

export interface ValidationResult {
  isValid: boolean
  balanceCheck: BalanceCheck
  issues?: ValidationIssue[]
}

interface ValidationViewProps {
  validationResult: ValidationResult
  onRerun?: () => void
}

const ValidationView: React.FC<ValidationViewProps> = ({
  validationResult,
  onRerun
}) => {

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />
      default:
        return <AlertTriangle className="w-4 h-4 text-blue-500" />
    }
  }

  const actions = onRerun ? (
    <button 
      onClick={onRerun}
      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center space-x-2 font-medium"
    >
      <RefreshCw className="w-4 h-4" />
      <span>Re-run Validation</span>
    </button>
  ) : undefined

  return (
    <WorkPanelWrapper title="Validation Results" actions={actions}>
      <div className="">

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold mb-2 text-gray-900">Balance Check</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Beginning Balance:</span>
                  <span className="font-mono">
                    ${validationResult.balanceCheck.beginningBalance.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Ending Balance:</span>
                  <span className="font-mono">
                    ${validationResult.balanceCheck.endingBalance.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Expected Ending:</span>
                  <span className="font-mono">
                    ${validationResult.balanceCheck.expectedEnding.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Difference:</span>
                  <span className={`font-mono ${
                    validationResult.balanceCheck.difference !== 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    ${Math.abs(validationResult.balanceCheck.difference).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold mb-2 text-gray-900">Transaction Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Credits:</span>
                  <span className="font-mono">
                    ${validationResult.balanceCheck.totalCredits.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Debits:</span>
                  <span className="font-mono">
                    ${validationResult.balanceCheck.totalDebits.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Transaction Count:</span>
                  <span className="font-mono">
                    {validationResult.balanceCheck.transactionCount}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold mb-2 text-gray-900">Issues Found</h3>
              <div className="space-y-2">
                {validationResult.issues && validationResult.issues.length > 0 ? (
                  validationResult.issues.map((issue, index) => (
                    <div key={index} className="flex items-start space-x-2 p-2 bg-white rounded border border-gray-200">
                      {getSeverityIcon(issue.severity)}
                      <div className="flex-1">
                        <div className="text-sm font-medium">{issue.field}</div>
                        <div className="text-xs text-gray-600">{issue.message}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No issues detected</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </WorkPanelWrapper>
  )
}

export default ValidationView
export type { ValidationIssue, BalanceCheck }
